// Builds a draw.io (diagrams.net) model for the flowchart.
//
// draw.io's File > Open only accepts SVGs that carry a diagram in the root
// <svg content="..."> attribute (its "editable SVG" format). Embedding this
// model makes the exported SVG open in draw.io as editable shapes and
// connectors, while every other viewer still renders the plain SVG drawing.

import { getOvalVisualRect } from '../floatingEdgeGeometry.ts';
import type { Rect } from '../floatingEdgeGeometry.ts';
import { NODE_HEIGHT, NODE_WIDTH, SWIMLANE_HEIGHT } from '../flowchartDimensions.ts';

// Minimal structural view of the React Flow nodes/edges FlowchartViewer
// keeps in state; only the fields the export reads.
export interface ExportNode {
  id: string;
  type?: string;
  hidden?: boolean;
  position?: { x?: number; y?: number };
  width?: number;
  height?: number;
  measured?: { width?: number; height?: number };
  data?: {
    label?: string;
    actor?: string;
    nodeType?: string;
    nodeDescription?: string;
    isMapped?: boolean;
    width?: number;
    height?: number;
    index?: number;
  };
}

export interface ExportEdge {
  id: string;
  source: string;
  target: string;
  hidden?: boolean;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  markerEnd?: unknown;
  markerStart?: unknown;
  style?: { stroke?: string; strokeWidth?: number | string; strokeDasharray?: string };
  data?: {
    label?: string;
    waypoints?: Array<{ x: number; y: number } | null | undefined>;
  };
}

export type BadgeKind = 'risk' | 'control' | 'mapped-control' | 'system' | 'document' | 'overflow';

// A badge drawn on a step (risk triangle, control hexagon, system cylinder,
// document folder, mapped-control tag or "+N" chip), measured from the canvas.
export interface ExportBadge {
  nodeId: string;
  kind: BadgeKind;
  /** Flow-coordinate bounding box of the badge's shape. */
  rect: Rect;
  /** Text shown on the badge on screen (may be truncated). */
  text: string;
  /** Full name / description, used as the draw.io tooltip. */
  tooltip?: string;
  fill?: string;
  stroke?: string;
  fontColor?: string;
  isKey?: boolean;
}

export function escapeXml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Cell labels use html=1, so user text is HTML-escaped first and then
// XML-escaped again as an attribute value.
function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function num(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function nodeRect(node: ExportNode): Rect {
  return {
    x: num(node.position?.x, 0),
    y: num(node.position?.y, 0),
    width: num(node.measured?.width ?? node.width, NODE_WIDTH),
    height: num(node.measured?.height ?? node.height, NODE_HEIGHT),
  };
}

function geometry(r: Rect): string {
  return `<mxGeometry x="${r.x}" y="${r.y}" width="${r.width}" height="${r.height}" as="geometry"/>`;
}

function vertex(id: string, value: string, style: string, r: Rect, parent = '1', tooltip?: string): string {
  const tooltipAttr = tooltip ? ` tooltip="${escapeXml(tooltip)}"` : '';
  return `<mxCell id="${escapeXml(id)}" value="${escapeXml(value)}" style="${style}" vertex="1" parent="${escapeXml(parent)}"${tooltipAttr}>${geometry(r)}</mxCell>`;
}

function stepColors(isMapped: boolean): { fill: string; stroke: string } {
  return isMapped
    ? { fill: '#ffffff', stroke: '#3b82f6' }
    : { fill: '#fef3c7', stroke: '#fbbf24' };
}

function swimlaneCell(node: ExportNode): string {
  const r: Rect = {
    x: num(node.position?.x, 0),
    y: num(node.position?.y, 0),
    width: num(node.data?.width, 0) || nodeRect(node).width,
    height: num(node.data?.height, 0) || SWIMLANE_HEIGHT,
  };
  const fill = num(node.data?.index, 0) % 2 === 0 ? '#f9fafb' : '#ffffff';
  const style = [
    'rounded=0', 'whiteSpace=wrap', 'html=1', `fillColor=${fill}`, 'strokeColor=#d1d5db',
    'align=left', 'verticalAlign=top', 'spacingLeft=16', 'spacingTop=12',
    'fontStyle=1', 'fontSize=14', 'fontColor=#1f2937',
  ].join(';');
  return vertex(`lane-${node.id}`, escapeHtml(node.data?.label || node.data?.actor || ''), style, r);
}

// The draw.io cell box for a step: ovals and diamonds are drawn inset from
// the React Flow node box, so badge offsets are computed against this.
function stepCellRect(node: ExportNode): Rect {
  const r = nodeRect(node);
  if (node.type === 'oval') return getOvalVisualRect(r);
  // The on-screen diamond is inset 10px inside the node box.
  if (node.type === 'decision') return { x: r.x + 10, y: r.y + 10, width: r.width - 20, height: r.height - 20 };
  return r;
}

function stepCell(node: ExportNode): string {
  const data = node.data || {};
  const r = stepCellRect(node);
  const description = escapeHtml(data.nodeDescription || '');

  if (node.type === 'oval') {
    const isStart = data.nodeType === 'start';
    const style = [
      'ellipse', 'whiteSpace=wrap', 'html=1',
      `fillColor=${isStart ? '#d1fae5' : '#fee2e2'}`,
      `strokeColor=${isStart ? '#10b981' : '#ef4444'}`,
      `fontColor=${isStart ? '#047857' : '#dc2626'}`,
      'strokeWidth=2', 'fontStyle=1', 'fontSize=14',
    ].join(';');
    return vertex(node.id, isStart ? 'Start' : 'End', style, r);
  }

  const { fill, stroke } = stepColors(Boolean(data.isMapped));
  if (node.type === 'decision') {
    const style = [
      'rhombus', 'whiteSpace=wrap', 'html=1', `fillColor=${fill}`, `strokeColor=${stroke}`,
      'strokeWidth=2', 'fontSize=12', 'fontStyle=1', 'fontColor=#1f2937', 'overflow=hidden',
    ].join(';');
    return vertex(node.id, description, style, r);
  }

  const actor = data.actor ? `<br><span style="font-size:12px;color:#2563eb;font-weight:normal"><b>Actor:</b> ${escapeHtml(data.actor)}</span>` : '';
  const style = [
    'rounded=1', 'arcSize=5', 'whiteSpace=wrap', 'html=1', `fillColor=${fill}`, `strokeColor=${stroke}`,
    'strokeWidth=2', 'fontSize=14', 'fontStyle=1', 'fontColor=#111827',
    'align=left', 'spacingLeft=12', 'spacingRight=12',
    // The canvas clamps descriptions to two lines; clip long text to the
    // step box instead of letting it spill over badges and other lanes.
    'overflow=hidden',
  ].join(';');
  return vertex(node.id, `${description}${actor}`, style, r);
}

const BADGE_SHAPES: Record<BadgeKind, string> = {
  risk: 'triangle;direction=north;verticalAlign=bottom;spacingBottom=1;fontSize=11;fontStyle=1',
  control: 'shape=hexagon;perimeter=hexagonPerimeter2;direction=south;size=0.375;fontSize=8;fontStyle=1',
  'mapped-control': 'rounded=1;arcSize=20;fontSize=10;fontStyle=1',
  system: 'shape=cylinder3;boundedLbl=1;backgroundOutline=1;size=7;fontSize=9;fontStyle=1',
  document: 'shape=folder;tabWidth=25;tabHeight=5;tabPosition=right;fontSize=9;fontStyle=1',
  overflow: 'rounded=1;arcSize=50;fontSize=10;fontStyle=1',
};

function badgeCell(badge: ExportBadge, index: number, parentRect: Rect): string {
  const style = [
    BADGE_SHAPES[badge.kind],
    'whiteSpace=wrap', 'html=1', 'overflow=hidden',
    `fillColor=${badge.fill || '#ffffff'}`,
    `strokeColor=${badge.stroke || 'none'}`,
    `fontColor=${badge.fontColor || '#111827'}`,
    badge.isKey ? 'strokeWidth=2' : 'strokeWidth=1.5',
  ].join(';');
  const label = escapeHtml(badge.text).replace(/\n/g, '<br>');
  // Child cells use coordinates relative to their parent step, so moving the
  // step in draw.io carries its badges along.
  const rect: Rect = {
    x: Math.round((badge.rect.x - parentRect.x) * 100) / 100,
    y: Math.round((badge.rect.y - parentRect.y) * 100) / 100,
    width: Math.round(badge.rect.width * 100) / 100,
    height: Math.round(badge.rect.height * 100) / 100,
  };
  return vertex(`badge-${badge.nodeId}-${index}`, label, style, rect, badge.nodeId, badge.tooltip);
}

// Fixed connection point on the shape for a React Flow handle id ("right",
// "bottom", legacy "left-source", ...). Floating edges have no handle and let
// draw.io pick the nearest side, as the canvas does.
function handlePoint(handleId: string | null | undefined): { x: number; y: number } | null {
  const id = String(handleId || '').toLowerCase();
  if (id.includes('right')) return { x: 1, y: 0.5 };
  if (id.includes('left')) return { x: 0, y: 0.5 };
  if (id.includes('top')) return { x: 0.5, y: 0 };
  if (id.includes('bottom')) return { x: 0.5, y: 1 };
  return null;
}

function edgeCell(edge: ExportEdge, nodeIds: Set<string>): string {
  if (!nodeIds.has(String(edge.source)) || !nodeIds.has(String(edge.target))) return '';
  const stroke = edge.style?.stroke || '#000000';
  const width = edge.style?.strokeWidth || 2;
  const exit = handlePoint(edge.sourceHandle);
  const entry = handlePoint(edge.targetHandle);
  const style = [
    'edgeStyle=orthogonalEdgeStyle', 'rounded=1', 'orthogonalLoop=1', 'html=1',
    `strokeColor=${stroke}`, `strokeWidth=${width}`,
    edge.markerEnd ? 'endArrow=block;endFill=1' : 'endArrow=none',
    edge.markerStart ? 'startArrow=block;startFill=1' : 'startArrow=none',
    edge.style?.strokeDasharray ? 'dashed=1' : '',
    'fontSize=10', 'labelBackgroundColor=#ffffff',
    exit ? `exitX=${exit.x};exitY=${exit.y};exitDx=0;exitDy=0` : '',
    entry ? `entryX=${entry.x};entryY=${entry.y};entryDx=0;entryDy=0` : '',
  ].filter(Boolean).join(';');

  const rawWaypoints = edge.data?.waypoints;
  const waypoints = Array.isArray(rawWaypoints)
    ? rawWaypoints.filter(
      (p): p is { x: number; y: number } => !!p && Number.isFinite(p.x) && Number.isFinite(p.y)
    )
    : [];
  const points = waypoints.length
    ? `<Array as="points">${waypoints.map((p) => `<mxPoint x="${p.x}" y="${p.y}"/>`).join('')}</Array>`
    : '';

  return `<mxCell id="${escapeXml(`edge-${edge.id}`)}" value="${escapeXml(escapeHtml(edge.data?.label || ''))}" style="${style}"`
    + ` edge="1" parent="1" source="${escapeXml(edge.source)}" target="${escapeXml(edge.target)}">`
    + `<mxGeometry relative="1" as="geometry">${points}</mxGeometry></mxCell>`;
}

/** Returns an uncompressed draw.io `<mxfile>` document for the flowchart. */
export function buildDrawioModel({ nodes, edges, name, badges }: {
  nodes: ExportNode[] | null | undefined;
  edges: ExportEdge[] | null | undefined;
  name?: string;
  badges?: ExportBadge[] | null;
}): string {
  const visible = (nodes || []).filter((n): n is ExportNode => !!n && !n.hidden);
  const lanes = visible.filter((n) => n.type === 'swimlane');
  const steps = visible.filter((n) => n.type !== 'swimlane');
  const stepIds = new Set(steps.map((n) => String(n.id)));
  const stepRects = new Map(steps.map((n) => [String(n.id), stepCellRect(n)]));
  const badgeCells = (badges || []).map((badge, i) => {
    const parentRect = stepRects.get(String(badge.nodeId));
    return parentRect ? badgeCell(badge, i, parentRect) : '';
  });

  const cells = [
    '<mxCell id="0"/>',
    '<mxCell id="1" parent="0"/>',
    ...lanes.map(swimlaneCell),
    ...steps.map(stepCell),
    ...badgeCells,
    ...(edges || []).filter((e): e is ExportEdge => !!e && !e.hidden).map((e) => edgeCell(e, stepIds)),
  ];

  return '<mxfile host="Skeptics">'
    + `<diagram id="flowchart" name="${escapeXml(name || 'Flowchart')}">`
    + '<mxGraphModel grid="1" gridSize="10" guides="1" connect="1" arrows="1" page="0">'
    + `<root>${cells.join('')}</root>`
    + '</mxGraphModel></diagram></mxfile>';
}
